/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CdrSortableFieldRow.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the CDR Settings module.
----------------------------------------------------------------*/


import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import type { CdrField } from '../types';

interface Props {
    id: string;
    index: number;
    field: CdrField;
    onChange: (updated: Partial<CdrField>) => void;
    onRemove: () => void;
}

export function CdrSortableFieldRow({ id, index, field, onChange, onRemove }: Props) {
    const { t } = useI18n();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const isPersisted = Boolean(field.id);

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={cn(
                'border-b border-border/60 bg-background',
                isDragging && 'shadow-lg ring-1 ring-primary/40 bg-muted/60',
            )}
        >
            <td className="px-2 py-3 align-middle">
                <div className="flex items-center justify-center gap-2">
                    <button type="button" className="cursor-grab active:cursor-grabbing rounded p-1 hover:bg-muted" {...attributes} {...listeners}>
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <span className="text-sm font-medium text-muted-foreground">{index + 1}</span>
                </div>
            </td>
            <td className="px-2 py-3 align-top">
                <Input
                    value={field.varName}
                    onChange={(e) => onChange({ varName: e.target.value })}
                    placeholder="uuid"
                    disabled={isPersisted}
                    title={isPersisted ? t('cdrSettings.tooltips.systemFieldLocked') : undefined}
                />
            </td>
            <td className="px-2 py-3 align-top">
                <Input value={field.aliasName} onChange={(e) => onChange({ aliasName: e.target.value })} placeholder="" />
            </td>
            <td className="px-2 py-3 text-center align-middle">
                <Toggle id={`quote-${id}`} value={field.quote} onChange={(v) => onChange({ quote: v })} />
            </td>
            <td className="px-2 py-3 text-center align-middle">
                <Toggle id={`enabled-${id}`} value={field.enabled} onChange={(v) => onChange({ enabled: v })} />
            </td>
            <td className="px-2 py-3 align-middle">
                <div className="flex justify-end">
                    <SimpleTooltip
                        content={isPersisted ? t('cdrSettings.tooltips.systemFieldDelete') : t('common.delete')}
                        position="top"
                    >
                        <button
                            type="button"
                            onClick={onRemove}
                            disabled={isPersisted}
                            className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </SimpleTooltip>
                </div>
            </td>
        </tr>
    );
}
